import { useState } from "react";
import { Link } from "react-router";
import { AlertTriangle, ArrowRight, Bell, Clock, Cog, RotateCcw, X } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { STATUS_META, STATUS_PHASES } from "@/components/prosthesis/prosthesis-meta";
import { isOverdue } from "@/lib/dashboard-lab";
import type { IncomingOrderRow } from "@/components/production/production-types";
import { PainelPedidosDoLab } from "@/components/organization/painel/PainelPedidosDoLab";
import { StatCard } from "./StatCard";
import { rotuloDoPrazo, type PrazoDoLaboratorio } from "@bookdental/schemas";

const producaoStatuses = STATUS_PHASES.find((p) => p.key === "producao")!.statuses;

type Painel = "pendentes" | "producao" | "retrabalho" | "atrasados";

const PAINEL: Record<Painel, { titulo: string; pagina: string }> = {
  pendentes: { titulo: "Pedidos pendentes", pagina: "/lab/pedidos" },
  producao: { titulo: "Em produção", pagina: "/lab/producao" },
  retrabalho: { titulo: "Em retrabalho", pagina: "/lab/producao" },
  atrasados: { titulo: "Atrasados", pagina: "/lab/producao" },
};

/**
 * Os números do dia do laboratório. Clicar num cartão abre a lista dele logo abaixo — o mesmo
 * painel de /organizacoes —, em vez de levar pra outra tela; a página completa fica a um link.
 * Notificações continua indo pra lista de notificações.
 */
export function LabDashboardStats({ prazo }: { prazo: PrazoDoLaboratorio }) {
  const incomingQuery = trpc.prosthesis.incoming.list.useQuery();
  const unread = trpc.notification.unreadCount.useQuery();
  const [painel, setPainel] = useState<Painel | null>(null);
  const alternar = (p: Painel) => setPainel((atual) => (atual === p ? null : p));
  const cartao = (p: Painel) => ({ onClick: () => alternar(p), selecionado: painel === p });

  const orders = (incomingQuery.data ?? []) as IncomingOrderRow[];
  const awaitingConfirmationCount = orders.filter((o) => o.status === "AGUARDANDO_CONFIRMACAO").length;
  const inProductionCount = orders.filter((o) => producaoStatuses.includes(o.status)).length;
  // Retrabalho é um evento (ver ProsthesisReworkRequest), não um status — conta pedido com ao
  // menos um serviço em retrabalho aberto.
  const reworkCount = orders.filter((o) => o.services.some((s) => s.hasOpenRework)).length;
  const unreadCount = unread.data?.count ?? 0;
  const overdueCount = orders.filter((o) => isOverdue(o, prazo)).length;

  return (
    <div className="space-y-4">
      {/* Dois por linha no celular: com o cartão compacto, um por linha desperdiçava metade da
          largura e ainda pedia rolagem pra ver os cinco. */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-2 sm:gap-3 lg:grid-cols-3 xl:grid-cols-5">
        <StatCard
          icon={Clock}
          label="Pendentes"
          value={awaitingConfirmationCount}
          hint="Pedidos ainda sem resposta"
          accent={STATUS_META.AGUARDANDO_CONFIRMACAO.color}
          loading={incomingQuery.isLoading}
          {...cartao("pendentes")}
        />
        <StatCard
          icon={Cog}
          label="Em Produção"
          value={inProductionCount}
          hint="Serviços aceitos, ainda não concluídos"
          accent={STATUS_META.EM_PRODUCAO.color}
          loading={incomingQuery.isLoading}
          {...cartao("producao")}
        />
        <StatCard
          icon={RotateCcw}
          label="Retrabalho"
          value={reworkCount}
          hint={reworkCount === 0 ? "Nenhum pedido em retrabalho" : "Solicitação de retrabalho em aberto"}
          accent="#f43f5e"
          loading={incomingQuery.isLoading}
          {...cartao("retrabalho")}
        />
        <StatCard
          icon={AlertTriangle}
          label="Atrasados"
          value={overdueCount}
          hint={overdueCount === 0 ? "Nenhum pedido atrasado" : `Prazo vencido (padrão de ${rotuloDoPrazo(prazo)})`}
          accent="#ef4444"
          loading={incomingQuery.isLoading}
          {...cartao("atrasados")}
        />
        {/* Só no desktop: no celular ele ficava sozinho na última linha, com metade da largura
            vazia, e não é informação que se acompanha do telefone — o sino do topo já avisa. */}
        <StatCard
          className="hidden md:block"
          icon={Bell}
          label="Notificações"
          value={unreadCount}
          hint="Não lidas"
          accent="#2457ff"
          href="/notificacoes"
          loading={unread.isLoading}
        />
      </div>

      {painel && (
        <section className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-border dark:bg-card">
          <div className="flex items-center justify-between gap-2 border-b border-zinc-100 px-4 py-2.5 dark:border-border">
            <h2 className="text-sm font-semibold text-zinc-900 dark:text-foreground">{PAINEL[painel].titulo}</h2>
            <div className="flex items-center gap-1">
              <Link
                to={PAINEL[painel].pagina}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[13px] font-medium text-brand-700 hover:bg-brand-50 dark:text-primary dark:hover:bg-primary/10"
              >
                Ver todos
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              <button
                type="button"
                onClick={() => setPainel(null)}
                aria-label="Fechar"
                className="flex h-8 w-8 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-muted"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="p-4">
            <PainelPedidosDoLab filtro={painel} prazo={prazo} />
          </div>
        </section>
      )}
    </div>
  );
}
